import { useLayoutEffect, useRef } from 'react'
import { pulseRevealClass } from './revealClass'

/**
 * Cross-dissolves the whole page when the favorite-team color changes *while
 * the app is interactive* — e.g. the user picks or switches their favorite
 * team mid-session — reusing the shared `.branding-reveal` body fade
 * (globals.scss). The team gradient lives on `body` background and is mostly
 * occluded by content, so fading it in isolation is imperceptible; the
 * whole-content dissolve is what actually reads.
 *
 * The baseline is captured on the *first ready render*, not on mount: the
 * team color's initial resolution can land in the same commit that
 * `isAppReady` flips true, so keying the skip on mount alone would dissolve
 * the page on initial load. Gating the baseline on readiness means only a
 * genuine change *after* the app is interactive pulses. While not ready the
 * baseline tracks the current color, so returning to ready (e.g. a logout →
 * re-login cycle) with an unchanged color never pulses either.
 */
export function useTeamGradientReveal(teamColor: string | undefined, isAppReady: boolean) {
  const baselineColor = useRef<string | undefined>(undefined)
  const hasBaseline = useRef(false)

  useLayoutEffect(() => {
    // Not interactive yet: keep the baseline synced to whatever the page is
    // loading with, but never pulse — the initial resolution happens behind
    // the loading overlay.
    if (!isAppReady) {
      baselineColor.current = teamColor
      return
    }

    // First ready render: the color the app became interactive with is the
    // baseline, not a change to dissolve for. Covers the initial load even
    // when color and readiness resolve together.
    if (!hasBaseline.current) {
      hasBaseline.current = true
      baselineColor.current = teamColor
      return
    }

    const prev = baselineColor.current
    baselineColor.current = teamColor
    if (prev === teamColor || !teamColor) return

    return pulseRevealClass()
  }, [teamColor, isAppReady])
}
